import type {
  WorkPolicyFormErrors,
  WorkPolicyFormValue
} from "./hooks/use-work-policy-form";
import { isCalendarDate } from "./calendar-date";
import {
  isCalendarRangeWithinMonths,
  MAX_FLEXIBLE_DATE_RANGE_MONTHS
} from "../work-type-shared/calendar-date-range";

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isClockTime(value: string) {
  return CLOCK.test(value);
}

export function calculateAutomaticBreakMinutes(
  plannedWorkMinutes: number,
  enabled: boolean,
  workMinutesPerRule: number,
  breakMinutesPerRule: number,
  waiveBreakAtFourHours = false
) {
  if (!enabled || waiveBreakAtFourHours || workMinutesPerRule <= 0) return 0;
  return Math.floor(plannedWorkMinutes / workMinutesPerRule) * breakMinutesPerRule;
}

export function calculateExpectedEndTime(
  startTime: string,
  plannedWorkMinutes: number,
  automaticBreakMinutes: number
) {
  if (!isClockTime(startTime)) return null;
  const [hour, minute] = startTime.split(":").map(Number);
  const total = hour * 60 + minute + plannedWorkMinutes + automaticBreakMinutes;
  const normalized = total % (24 * 60);
  return {
    time: `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(
      normalized % 60
    ).padStart(2, "0")}`,
    crossesDay: total >= 24 * 60
  };
}

function validateBasePeriod(value: WorkPolicyFormValue, errors: WorkPolicyFormErrors) {
  if (!isCalendarDate(value.effectiveStartDate)) {
    errors.effectiveStartDate = "적용 시작일을 YYYY-MM-DD 형식으로 입력하세요.";
  }
  if (!isCalendarDate(value.effectiveEndDate)) {
    errors.effectiveEndDate = "적용 종료일을 YYYY-MM-DD 형식으로 입력하세요.";
  } else if (
    isCalendarDate(value.effectiveStartDate) &&
    value.effectiveEndDate < value.effectiveStartDate
  ) {
    errors.effectiveEndDate = "적용 종료일은 시작일과 같거나 이후여야 합니다.";
  } else if (
    isCalendarDate(value.effectiveStartDate) &&
    !isCalendarRangeWithinMonths(
      value.effectiveStartDate,
      value.effectiveEndDate,
      MAX_FLEXIBLE_DATE_RANGE_MONTHS
    )
  ) {
    errors.effectiveEndDate = "적용 기간은 시작일 기준 최대 6개월까지 지정할 수 있습니다.";
  }
}

function validateStaggered(value: WorkPolicyFormValue, errors: WorkPolicyFormErrors) {
  if (value.policyKind === "APPROVAL") {
    if (value.staggeredStartTimes.length === 0) {
      errors.staggeredStartTimes = "허용 출근 시간을 한 개 이상 입력하세요.";
      return;
    }
    if (value.staggeredStartTimes.some(time => !isClockTime(time))) {
      errors.staggeredStartTimes = "허용 출근 시간을 HH:mm 형식으로 입력하세요.";
    } else if (new Set(value.staggeredStartTimes).size !== value.staggeredStartTimes.length) {
      errors.staggeredStartTimes = "허용 출근 시간에 중복 값이 있습니다.";
    }
    return;
  }
  if (!isClockTime(value.baseDefaultStartTime)) {
    errors.baseDefaultStartTime = "고정 출근 시간을 HH:mm 형식으로 입력하세요.";
    return;
  }
  const workMinutes = Math.round(Number(value.dailyBaseHours) * 60);
  const automaticBreak = calculateAutomaticBreakMinutes(
    workMinutes,
    value.breakCalculationEnabled,
    Number(value.breakWorkMinutes),
    Number(value.breakMinutes)
  );
  if (
    calculateExpectedEndTime(value.baseDefaultStartTime, workMinutes, automaticBreak)
      ?.crossesDay
  ) {
    errors.baseDefaultStartTime =
      "계산된 퇴근 시간이 다음 날입니다. 출근·근로·휴게시간을 확인하세요.";
  }
}

function validateFlexible(value: WorkPolicyFormValue, errors: WorkPolicyFormErrors) {
  if (value.policyKind === "APPROVAL") return;
  const issues: string[] = [];
  if (value.schedule.length === 0) {
    errors.schedule = "BASE 탄력 일정은 한 행 이상 필요합니다.";
    return;
  }
  const workDates = new Set<string>();
  const weekdays = new Set<number>();
  const breakRule = Number(value.breakWorkMinutes);
  const breakMinutes = Number(value.breakMinutes);

  value.schedule.forEach((row, index) => {
    const rowName = `${index + 1}번 일정`;
    if (value.flexibleMode === "DAILY_DATE") {
      if (!row.workDate || !isCalendarDate(row.workDate)) {
        issues.push(`${rowName}: 날짜별 스케줄에는 유효한 날짜가 필요합니다.`);
      } else {
        if (workDates.has(row.workDate)) issues.push(`${rowName}: 날짜가 중복됩니다.`);
        workDates.add(row.workDate);
        if (
          isCalendarDate(value.effectiveStartDate) &&
          isCalendarDate(value.effectiveEndDate) &&
          (row.workDate < value.effectiveStartDate || row.workDate > value.effectiveEndDate)
        ) {
          issues.push(`${rowName}: 적용 기간 밖 날짜입니다.`);
        }
      }
      if (row.weekdayNo !== null) issues.push(`${rowName}: 요일 key를 함께 보낼 수 없습니다.`);
    } else {
      if (!row.weekdayNo || row.weekdayNo < 1 || row.weekdayNo > 7) {
        issues.push(`${rowName}: 주간 반복에는 1~7 요일이 필요합니다.`);
      } else {
        if (weekdays.has(row.weekdayNo)) issues.push(`${rowName}: 요일이 중복됩니다.`);
        weekdays.add(row.weekdayNo);
      }
      if (row.workDate !== null) issues.push(`${rowName}: 날짜 key를 함께 보낼 수 없습니다.`);
    }

    if (!row.workday) {
      if (row.startTime || row.plannedWorkMinutes > 0 || row.breakWaiver) {
        issues.push(`${rowName}: 휴무일에는 출근·근로·휴게 detail을 저장하지 않습니다.`);
      }
      return;
    }
    if (!row.startTime || !isClockTime(row.startTime)) {
      issues.push(`${rowName}: 출근 시간을 HH:mm 형식으로 입력하세요.`);
    }
    if (!Number.isInteger(row.plannedWorkMinutes) || row.plannedWorkMinutes < 1 || row.plannedWorkMinutes > 720) {
      issues.push(`${rowName}: 계획 근로시간은 1~720분 정수여야 합니다.`);
    }
    if (row.breakWaiver && row.plannedWorkMinutes !== 240) {
      issues.push(`${rowName}: 휴게 미사용은 정확히 4시간 근무에만 선택할 수 있습니다.`);
    }
    if (row.startTime && isClockTime(row.startTime) && row.plannedWorkMinutes > 0) {
      const automaticBreak = calculateAutomaticBreakMinutes(
        row.plannedWorkMinutes,
        value.breakCalculationEnabled,
        breakRule,
        breakMinutes,
        row.breakWaiver
      );
      if (
        calculateExpectedEndTime(row.startTime, row.plannedWorkMinutes, automaticBreak)
          ?.crossesDay
      ) {
        issues.push(`${rowName}: 예상 종료 시간이 다음 날입니다.`);
      }
    }
  });

  if (issues.length > 0) errors.schedule = issues.join(" · ");
}

function validateSelective(value: WorkPolicyFormValue, errors: WorkPolicyFormErrors) {
  if (!value.selectiveCoreEnabled) return;
  if (!isClockTime(value.selectiveCoreStart) || !isClockTime(value.selectiveCoreEnd)) {
    errors.selectiveCoreStart = "의무 코어타임 시작과 종료를 HH:mm 형식으로 입력하세요.";
  } else if (value.selectiveCoreStart >= value.selectiveCoreEnd) {
    errors.selectiveCoreStart = "의무 코어타임 시작은 종료 이전이어야 합니다.";
  }
}

export function validateTypeSpecificWorkPolicy(value: WorkPolicyFormValue) {
  const errors: WorkPolicyFormErrors = {};
  if (value.policyKind === "BASE") validateBasePeriod(value, errors);
  if (value.workType === "staggered") validateStaggered(value, errors);
  if (value.workType === "flexible") validateFlexible(value, errors);
  if (value.workType === "selective") validateSelective(value, errors);
  return errors;
}
